import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  FormControlLabel,
  Switch,
  Slider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  useMediaQuery,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Save as SaveIcon,
  Lock as LockIcon,
  Schedule as ScheduleIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
  securityPolicyApi,
  SecurityAuditEntry,
  SecurityStatus,
} from 'shared/services/securityPolicyApi';

const fmt = (ts: string | Date) => new Date(ts).toLocaleString('es-CR');

/**
 * Configuración de Seguridad (super_admin). The password policy + login-lockout
 * thresholds are real: they persist to the platform SecurityPolicy and are
 * enforced on the backend. The audit log + status panel reflect real data.
 *
 * Infra protections (2FA-always-on, rate limiting, HTTPS, device trust) are
 * always-on and shown read-only — they are not fake toggles. IP allowlist,
 * concurrent sessions and log retention are marked "próximamente".
 */
export const SecuritySettings: React.FC = () => {
  const isXs = useMediaQuery('(max-width:599.95px)');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState<SecurityAuditEntry[]>([]);
  const [status, setStatus] = useState<SecurityStatus | null>(null);
  const [toast, setToast] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({
    open: false,
    msg: '',
    severity: 'success',
  });

  // Editable + persisted policy.
  const [pw, setPw] = useState({
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecial: false,
  });
  const [lockout, setLockout] = useState({ maxAttempts: 5, lockoutMinutes: 30 });

  useEffect(() => {
    let cancelled = false;
    securityPolicyApi
      .get()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setPw(res.data.policy.password);
          setLockout(res.data.policy.lockout);
          setAudit(res.data.recentAudit || []);
          setStatus(res.data.status);
        } else {
          setToast({ open: true, msg: res.message || 'No se pudo cargar la configuración', severity: 'error' });
        }
      })
      .catch(() => setToast({ open: true, msg: 'Error al cargar la configuración', severity: 'error' }))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await securityPolicyApi.update({ password: pw, lockout });
      if (res.success) {
        setToast({ open: true, msg: 'Política de seguridad guardada.', severity: 'success' });
      } else {
        setToast({ open: true, msg: res.message || 'No se pudo guardar la política.', severity: 'error' });
      }
    } catch (err) {
      // apiClient throws on non-2xx — surface it instead of leaving the button stuck.
      setToast({
        open: true,
        msg: err instanceof Error ? err.message : 'No se pudo guardar la política.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const passwordStrengthText = () => {
    const req: string[] = [];
    if (pw.requireUppercase) req.push('mayúsculas');
    if (pw.requireLowercase) req.push('minúsculas');
    if (pw.requireNumbers) req.push('números');
    if (pw.requireSpecial) req.push('caracteres especiales');
    return (
      `Las contraseñas deben tener al menos ${pw.minLength} caracteres` +
      (req.length > 0 ? ` e incluir: ${req.join(', ')}.` : '.')
    );
  };

  const statusIcon = (ok: boolean | undefined) =>
    ok ? <CheckCircleIcon color="success" sx={{ fontSize: 40 }} /> : <WarningIcon color="warning" sx={{ fontSize: 40 }} />;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configuración de Seguridad
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Las políticas de contraseña y de bloqueo por intentos fallidos se aplican en el servidor. Las
        protecciones de infraestructura se muestran como estado (solo lectura).
      </Typography>

      <Grid container spacing={3}>
        {/* Password Policies — editable + enforced */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <LockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Políticas de Contraseñas
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography gutterBottom>Longitud mínima: {pw.minLength} caracteres</Typography>
                <Slider
                  value={pw.minLength}
                  onChange={(_, value) => setPw((p) => ({ ...p, minLength: value as number }))}
                  min={6}
                  max={20}
                  step={1}
                  marks
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                <FormControlLabel
                  control={<Switch checked={pw.requireUppercase} onChange={(e) => setPw((p) => ({ ...p, requireUppercase: e.target.checked }))} />}
                  label="Requerir letras mayúsculas"
                />
                <FormControlLabel
                  control={<Switch checked={pw.requireLowercase} onChange={(e) => setPw((p) => ({ ...p, requireLowercase: e.target.checked }))} />}
                  label="Requerir letras minúsculas"
                />
                <FormControlLabel
                  control={<Switch checked={pw.requireNumbers} onChange={(e) => setPw((p) => ({ ...p, requireNumbers: e.target.checked }))} />}
                  label="Requerir números"
                />
                <FormControlLabel
                  control={<Switch checked={pw.requireSpecial} onChange={(e) => setPw((p) => ({ ...p, requireSpecial: e.target.checked }))} />}
                  label="Requerir caracteres especiales"
                />
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">{passwordStrengthText()}</Typography>
              </Alert>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Se aplica al crear cuenta, restablecer o cambiar contraseña. No afecta contraseñas ya existentes.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Session and Authentication */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Sesiones y Autenticación
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Intentos máximos de login"
                    type="number"
                    value={lockout.maxAttempts}
                    onChange={(e) => setLockout((l) => ({ ...l, maxAttempts: parseInt(e.target.value) || l.maxAttempts }))}
                    inputProps={{ min: 3, max: 20 }}
                    helperText="Bloquea la cuenta tras N intentos fallidos"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Bloqueo temporal (minutos)"
                    type="number"
                    value={lockout.lockoutMinutes}
                    onChange={(e) => setLockout((l) => ({ ...l, lockoutMinutes: parseInt(e.target.value) || l.lockoutMinutes }))}
                    inputProps={{ min: 1, max: 1440 }}
                    helperText="Duración del bloqueo"
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 2 }}>
                <FormControlLabel
                  control={<Switch checked disabled />}
                  label="Autenticación de dos factores (obligatoria para roles administrativos)"
                />
                <FormControlLabel
                  control={<Switch checked disabled />}
                  label="Permitir 'Recordarme' (confía en el dispositivo hasta 7 días)"
                />
                <FormControlLabel
                  control={<Switch checked={false} disabled />}
                  label="Permitir sesiones concurrentes (próximamente)"
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                El tiempo de sesión lo gestiona el servidor (token con refresco automático).
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* System Security — read-only infra status */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Seguridad del Sistema
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Protecciones siempre activas a nivel de infraestructura.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <FormControlLabel control={<Switch checked={!!status?.auditActive} disabled />} label="Registro de auditoría" />
                <FormControlLabel control={<Switch checked={!!status?.rateLimiting} disabled />} label="Límite de velocidad de requests" />
                <FormControlLabel control={<Switch checked={!!status?.httpsActive} disabled />} label="HTTPS (TLS en el borde)" />
                <FormControlLabel control={<Switch checked disabled />} label="Rastreo de dispositivos" />
                <FormControlLabel control={<Switch checked={false} disabled />} label="Lista blanca de IPs (próximamente)" />
                <FormControlLabel control={<Switch checked={false} disabled />} label="Retención de logs configurable (próximamente)" />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Audit Log — real recent events */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Registro de Auditoría Reciente
              </Typography>

              {audit.length === 0 ? (
                <Alert severity="info">No hay eventos de acceso recientes.</Alert>
              ) : isXs ? (
                /* Phone: stacked cards instead of the 3-column table. */
                <Stack spacing={1} sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {audit.map((log) => (
                    <Paper key={log.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Chip size="small" label={log.action} color={log.success ? 'primary' : 'error'} />
                        <Typography variant="caption" color="text.secondary">{fmt(log.timestamp)}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>{log.user}</Typography>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Acción</TableCell>
                        <TableCell>Usuario</TableCell>
                        <TableCell>Hora</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {audit.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            <Chip size="small" label={log.action} color={log.success ? 'primary' : 'error'} />
                          </TableCell>
                          <TableCell>{log.user}</TableCell>
                          <TableCell>{fmt(log.timestamp)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Security Status — computed from real state */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Estado de Seguridad</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    {statusIcon(status?.passwordPolicy)}
                    <Typography variant="body2">Políticas de Contraseña</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    {statusIcon(status?.httpsActive)}
                    <Typography variant="body2">Conexión Segura</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    {statusIcon(status?.auditActive)}
                    <Typography variant="body2">Auditoría Activa</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    {statusIcon(status?.bruteForceProtection)}
                    <Typography variant="body2">Protección Brute Force</Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          size="large"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {saving ? 'Guardando…' : 'Guardar Configuración'}
        </Button>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SecuritySettings;
